<template>
    <div class="reference-measurement">
        <template v-if="isReferenceValue">
            <span class="reference-measurement__value">{{ t('reports.detailsPage.referenceMeasurement.value') }}</span>
        </template>
        <template v-else>
            <HorizontalButton
                :text="t('reports.detailsPage.referenceMeasurement.label')"
                :icon-name="isReferenceValue ? 'check-square' : 'square'"
                :disabled="!userPermissions.canSetReferenceReports || !canBeSetAsReference"
                variant="primary"
                small
                @click="onToggleReferenceChange"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import HorizontalButton from '@/components/buttons/HorizontalButton.vue'
import { useI18n } from 'vue-i18n'
import { useGlobalConfirmationModal } from '@/composables/useGlobalConfirmationModal'
import { useReportSetReferenceMutation } from '@/api/mutations/reports/useReportSetReferenceMutation'
import { toRefs } from 'vue'
import { usePebbleToasts } from '@/composables/pebble/usePebbleToasts'
import { AxiosError } from 'axios'
import { usePermissions } from '@/composables/usePermissions'

const props = defineProps<{
    reportNumber: string
    subReportName?: string
    componentId: string
    isReferenceValue: boolean
    analysisType: number
    canBeSetAsReference?: boolean
}>()

const { reportNumber, isReferenceValue, subReportName, componentId, analysisType, canBeSetAsReference } = toRefs(props)

const { t } = useI18n()
const toasts = usePebbleToasts()
const { userPermissions } = usePermissions()
const triggerConfirmationModal = useGlobalConfirmationModal()

const { mutateAsync } = useReportSetReferenceMutation()

const onToggleReferenceChange = () => {
    triggerConfirmationModal({
        headline: t('reports.detailsPage.referenceMeasurement.modal.title'),
        content: t('reports.detailsPage.referenceMeasurement.modal.description', { subReportName: subReportName.value, technicalLocation: componentId.value }),
        confirmButtonText: t('reports.detailsPage.excludeModal.confirmButtonText'),
        onConfirm: async () => {
            try {
                await mutateAsync({ analyticalNumber: reportNumber.value, componentSid: componentId.value, analysisType: analysisType.value })
            } catch (err) {
                if (err instanceof AxiosError) {
                    toasts?.error({
                        message: t('common.errors.unknown'),
                        duration: 3000,
                    })
                }
            }
        },
    })
}
</script>

<style scoped lang="scss">
.reference-measurement {
    &__value {
        font-weight: 500;
        color: $pbl-text-muted;
        font-size: $default-font-size;
    }
}
</style>
